// ---------------------------------------------------------------------------
// A player's progression state: arena, balances, owned structures and lots,
// and the Hive layout
// ---------------------------------------------------------------------------
//
// Plain JSON, so it stores as-is (Firestore on the server, memory in the sim):
//
//   {
//     arena:        1,                    // never goes down
//     roundsPlayed: 0,                    // war rounds played in any war: the
//                                         // clock upgrade timers run on
//     bucks: 0, gems: 0,
//     raw:     { timber: 0, metal: 0, … },        // every RAW_RESOURCES key
//     refined: { Steel: 0, Concrete: 0 },         // used only with converters on
//     structures: {                       // owned types: one of each at most
//       'Lumber Mill': { level: 1, upgrade: null | { toLevel, finishAtRound } },
//     },
//     lots: { timber: 3 },                // owned copies by type, placed or not
//     hive: {                             // what's placed (hive.js checks it)
//       lots:       { [cell]: lotType },
//       structures: { [name]: { cell, rotation } },
//     },
//   }
//
// Every function in progression/ returns a NEW player and never mutates the
// one it was given.

import { RAW_RESOURCES, REFINED_RESOURCES } from './importPacing.js'

export function newPlayer() {
    return {
        arena: 1,
        roundsPlayed: 0,
        bucks: 0,
        gems: 0,
        raw: Object.fromEntries(RAW_RESOURCES.map(r => [r, 0])),
        refined: Object.fromEntries(REFINED_RESOURCES.map(r => [r, 0])),
        structures: {},
        lots: {},
        hive: { lots: {}, structures: {} },
    }
}

export const clonePlayer = player => JSON.parse(JSON.stringify(player))

// A bundle of resources: { bucks?, gems?, raw?: { timber: n }, refined?: { Steel: n } }.
export function canAfford(player, cost) {
    if ((cost.bucks ?? 0) > player.bucks) return false
    if ((cost.gems ?? 0) > player.gems) return false
    for (const [r, n] of Object.entries(cost.raw ?? {})) if (n > (player.raw[r] ?? 0)) return false
    for (const [r, n] of Object.entries(cost.refined ?? {})) if (n > (player.refined[r] ?? 0)) return false
    return true
}

// Adds a bundle (sign = -1 spends it). Spending more than the player has throws.
export function applyBundle(player, bundle, sign = 1) {
    if (sign < 0 && !canAfford(player, bundle)) throw new Error('Not enough resources')
    const out = clonePlayer(player)
    out.bucks += sign * (bundle.bucks ?? 0)
    out.gems += sign * (bundle.gems ?? 0)
    for (const [r, n] of Object.entries(bundle.raw ?? {})) {
        if (!RAW_RESOURCES.includes(r)) throw new Error(`Unknown raw resource "${r}"`)
        out.raw[r] += sign * n
    }
    for (const [r, n] of Object.entries(bundle.refined ?? {})) {
        if (!REFINED_RESOURCES.includes(r)) throw new Error(`Unknown refined resource "${r}"`)
        out.refined[r] += sign * n
    }
    return out
}

export const ownsStructure = (player, name) => !!player.structures[name]

export const ownedLots = (player, type) => player.lots[type] ?? 0

export const totalLotsOwned = player => Object.values(player.lots).reduce((a, n) => a + n, 0)

// Copies of a lot type sitting in the inventory, not on the Hive.
export function unplacedLots(player, type) {
    const placed = Object.values(player.hive.lots).filter(t => t === type).length
    return ownedLots(player, type) - placed
}
